import {
  Alert,
  AlertDescription,
  AlertIcon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useCustomerUserInvite } from "gen/endpoints/customer-user-invite/customer-user-invite";
import { CustomerUserInviteAPIDataRequest } from "gen/model/customerUserInviteAPIDataRequest";
import { UserRoleDescriptionAPIDataList } from "gen/model/userRoleDescriptionAPIDataList";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { useSelectedCustomerId } from "shared/hooks/useSelectedCustomerId";
import { InfoBoxIcon } from "shared/icons/InfoBoxIcon";

interface InviteUserModalProps {
  roleDefinitions: UserRoleDescriptionAPIDataList;
  onCloseComplete: () => void;
  onSuccess: () => void;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  roleDefinitions,
  onSuccess,
  onCloseComplete,
}) => {
  const { t } = useTranslation(["common", "settings"]);
  const selectedCustomerId = useSelectedCustomerId();
  const { mutateAsync: inviteCustomerUser } = useCustomerUserInvite();

  const form = useForm<CustomerUserInviteAPIDataRequest>({});

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t("settings:user.invite.title")}
          <ModalCloseButton />
        </ModalHeader>
        <Form
          form={form}
          onSubmit={async (data) =>
            inviteCustomerUser({
              data: {
                customer_id: selectedCustomerId!,
                user_role: data.user_role,
                user_email: data.user_email,
              },
            })
          }
          onSuccess={onSuccess}
          successMessage={t("settings:user.invite.success")}
        >
          <ModalBody>
            <Alert
              status="info"
              mb="24px"
              data-ls-enabled={true}
              data-public={true}
            >
              <AlertIcon>
                <InfoBoxIcon />
              </AlertIcon>
              <AlertDescription>
                {t("settings:user.invite.alert")}
              </AlertDescription>
            </Alert>
            <FormTextInput
              title={t("common:user")}
              name="user_email"
              type="email"
            />
            <FormSelect title={t("common:role")} name="user_role">
              {useMemo(
                () =>
                  roleDefinitions.map(({ role, name }) => (
                    <SelectOption key={role} value={role}>
                      {name}
                    </SelectOption>
                  )),
                [roleDefinitions],
              )}
            </FormSelect>
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} />}
              confirm={
                <FormSubmitButton>
                  {t("settings:user.invite.sendInvitation")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default InviteUserModal;
